import { useEffect, useState } from "react";
import { Wifi, WifiOff, CloudUpload, AlertTriangle, RefreshCw } from "lucide-react";
import { subscribe, getStatus, syncQueue } from "@/lib/offlineSync";
import { listConflicts, resolveConflict } from "@/lib/idb";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function OfflineIndicator() {
  const [status, setStatus] = useState({ online: true, pending: 0, syncing: false });
  const [conflicts, setConflicts] = useState([]);
  const [showConflicts, setShowConflicts] = useState(false);

  useEffect(() => {
    const refreshConflicts = async () => setConflicts(await listConflicts());
    const unsubscribe = subscribe((s) => {
      setStatus((prev) => {
        // Toast on transitions
        if (prev.online && !s.online) toast.warning("Mode hors-ligne — vos saisies seront synchronisées au retour de la connexion.");
        if (!prev.online && s.online) toast.success("Connexion rétablie — synchronisation en cours…");
        if (prev.syncing && !s.syncing && s.pending === 0 && prev.pending > 0) {
          toast.success(`Synchronisation terminée : ${prev.pending} opération(s) envoyée(s).`);
          refreshConflicts();
        }
        return s;
      });
    });
    getStatus().then(setStatus);
    refreshConflicts();
    const interval = setInterval(refreshConflicts, 10000);
    return () => { unsubscribe(); clearInterval(interval); };
  }, []);

  const hasConflicts = conflicts.length > 0;
  const offline = !status.online;
  const pending = status.pending;

  if (!offline && pending === 0 && !hasConflicts) return null;

  return (
    <>
      <div
        data-testid="offline-indicator"
        className="fixed top-3 right-3 z-50 flex items-center gap-2"
      >
        {offline && (
          <div
            data-testid="offline-badge"
            className="flex items-center gap-2 px-3 py-1.5 bg-amber-500 text-white text-xs font-bold rounded-full shadow-md uppercase tracking-wider"
          >
            <WifiOff className="h-3.5 w-3.5" /> Hors-ligne
          </div>
        )}
        {!offline && pending > 0 && (
          <button
            onClick={() => syncQueue()}
            data-testid="pending-sync-badge"
            className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-full shadow-md uppercase tracking-wider transition-colors"
          >
            {status.syncing ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <CloudUpload className="h-3.5 w-3.5" />}
            {status.syncing ? "Synchronisation…" : `${pending} en attente`}
          </button>
        )}
        {offline && pending > 0 && (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 text-white text-xs font-bold rounded-full shadow-md uppercase tracking-wider">
            <CloudUpload className="h-3.5 w-3.5" /> {pending} en file
          </div>
        )}
        {hasConflicts && (
          <button
            onClick={() => setShowConflicts(true)}
            data-testid="conflict-badge"
            className="flex items-center gap-2 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-full shadow-md uppercase tracking-wider transition-colors"
          >
            <AlertTriangle className="h-3.5 w-3.5" /> {conflicts.length} conflit(s)
          </button>
        )}
      </div>

      {offline && (
        <div
          data-testid="offline-banner"
          className="fixed bottom-0 left-0 right-0 z-40 bg-amber-500 text-white text-sm py-2 px-4 text-center flex items-center justify-center gap-3 shadow-lg"
        >
          <WifiOff className="h-4 w-4" />
          <span>
            <strong>Mode hors-ligne actif.</strong> Vos saisies sont conservées et envoyées dès le retour de la connexion.
          </span>
        </div>
      )}

      <Dialog open={showConflicts} onOpenChange={setShowConflicts}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="conflicts-dialog">
          <DialogHeader>
            <DialogTitle className="font-barlow text-2xl flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" /> Conflits de synchronisation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Ces opérations ont été rejetées par le serveur car la donnée a été modifiée pendant que vous étiez hors-ligne.
              Vérifiez si vos modifications doivent être ressaisies.
            </p>
            {conflicts.length === 0 ? (
              <div className="text-center text-slate-400 py-8">Aucun conflit.</div>
            ) : (
              conflicts.map((c) => (
                <div key={c.id} className="border border-red-200 bg-red-50 rounded-md p-3 text-xs">
                  <div className="font-bold text-red-700 uppercase tracking-wider">
                    {c.method?.toUpperCase()} {c.url}
                  </div>
                  <div className="mt-1 text-slate-700">
                    Date : {new Date(c.ts).toLocaleString("fr-FR")}
                  </div>
                  <details className="mt-2">
                    <summary className="cursor-pointer text-slate-600">Voir les détails</summary>
                    <pre className="mt-1 text-[10px] bg-white border border-slate-200 rounded p-2 overflow-x-auto">
                      {JSON.stringify(c.local, null, 2)}
                    </pre>
                  </details>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={async () => {
                      await resolveConflict(c.id);
                      setConflicts(await listConflicts());
                    }}
                  >
                    Marquer résolu
                  </Button>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
